import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApiClientError,
  DecorKindSchema,
  type ApiClientErrorCode,
  type DecorKind,
  type DecorListItem,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { useCatalogFilters } from '@/features/handles-catalog/useCatalogFilters';
import { DecorsCatalogView, type DecorsCatalogState } from './DecorsCatalogView';
import type { DecorFilterField } from './DecorFilterChips';
import { DECOR_FILTER_FIELDS } from './decorFilterFields';
import { useDecorImageUrls } from './useDecorImageUrls';
import { useDecorFacets } from './useDecorFacets';
import { decorFilterToQuery } from './decorFilterParams';

/**
 * Container Экрана 2.2.6 (фильтр + список каталога декора). См. ADR-0005.
 *
 * PR-W1 (поток v2): живёт на `/decors/:kind/catalog` — ветка «Полистай каталог»
 * метод-пикера (2.2.2). Это объединённый экран фильтр+список: чипы фильтра +
 * «Найти» → список карточек inline (никуда не навигирует). Фильтр живёт ТОЛЬКО
 * здесь — подбор по фото/названию идёт до фильтра и его не учитывает.
 *
 * Связывает URL-state, локальный preview-state чипов, fetch через apiClient →
 * передаёт данные Presenter'у. Использует generic `useCatalogFilters` из Phase 5
 * (живёт в `handles-catalog/` — это hook, не feature-компонент, кросс-feature
 * import оправдан и согласован).
 *
 * Fetch-стратегия: idle когда фильтров нет, fetch при ≥1 фильтре или клике
 * «Найти». Совпадает с Block 4 HandlesCatalog UX. См. BACKLOG (Phase 7+) для
 * рассмотрения «browse без фильтра» в качестве UX-альтернативы.
 *
 * Cursor pagination: «Показать ещё» подгружает следующую страницу через
 * `apiClient.decors.list({ cursor })`. Items накапливаются (а не перезаписывают);
 * сброс при смене фильтров.
 */
const DEFAULT_LIMIT = 12;

export function DecorsCatalog() {
  const navigate = useNavigate();
  const params = useParams<{ kind: string }>();
  const kindParse = DecorKindSchema.safeParse(params.kind);
  // Silent fallback при неизвестном kind — соответствует Block 4 B-012 паттерну.
  const kind: DecorKind = kindParse.success ? kindParse.data : 'facade';

  const {
    filters: appliedFilters,
    hasFilters,
    apply,
    reset,
  } = useCatalogFilters(DECOR_FILTER_FIELDS);
  const appliedSubs = useMemo(
    () => appliedFilters.subcategories ?? [],
    [appliedFilters.subcategories],
  );
  const appliedBrands = useMemo(() => appliedFilters.brands ?? [], [appliedFilters.brands]);
  const appliedTypes = useMemo(() => appliedFilters.types ?? [], [appliedFilters.types]);

  // Preview-state чипов до клика «Найти». Инициализируется applied-фильтрами
  // для поддержки deep-link.
  const [selectedSubs, setSelectedSubs] = useState<readonly string[]>(appliedSubs);
  const [selectedBrands, setSelectedBrands] = useState<readonly string[]>(appliedBrands);
  const [selectedTypes, setSelectedTypes] = useState<readonly string[]>(appliedTypes);

  // Опции фасета ИЗ ДАННЫХ (ADR-0054); Бренд/Тип сужены выбранной подкатегорией.
  const {
    subcategoryOptions,
    brandOptions,
    typeOptions,
    loading: facetsLoading,
  } = useDecorFacets(kind, selectedSubs, selectedBrands);

  // Pruning невалидных выбранных Бренд/Тип после загрузки опций (см. DecorsFilter).
  useEffect(() => {
    if (facetsLoading) return;
    setSelectedBrands((prev) => prev.filter((b) => brandOptions.includes(b)));
    setSelectedTypes((prev) => prev.filter((t) => typeOptions.includes(t)));
  }, [facetsLoading, brandOptions, typeOptions]);

  const [items, setItems] = useState<readonly DecorListItem[]>([]);
  // Фото-2 (PR-DATA4): signed-URL для фото видимых карточек.
  const photoKeys = useMemo(() => items.map((i) => i.primaryPhotoKey), [items]);
  const { urls: imageUrls } = useDecorImageUrls(photoKeys);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | null>(null);

  // Json-stringify дешевле чем reactive deps на массивах для useEffect MVP.
  // Phase 7+ (TanStack Query) даст автоматический dedup через queryKey.
  const filtersKey = JSON.stringify(appliedFilters);

  useEffect(() => {
    if (!hasFilters) {
      setItems([]);
      setNextCursor(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setItems([]);
    setNextCursor(null);
    let cancelled = false;
    apiClient.decors
      .list(kind, {
        subcategories: [...appliedSubs],
        brands: [...appliedBrands],
        types: [...appliedTypes],
        limit: DEFAULT_LIMIT,
      })
      .then(
        (res) => {
          if (cancelled) return;
          setItems(res.items);
          setNextCursor(res.nextCursor);
          setLoading(false);
        },
        (err: unknown) => {
          if (cancelled) return;
          // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
          const apiErr =
            err instanceof ApiClientError
              ? { code: err.code, message: err.message }
              : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
          setError(apiErr);
          setLoading(false);
        },
      );
    return () => {
      cancelled = true;
    };
  }, [kind, hasFilters, filtersKey, appliedSubs, appliedBrands, appliedTypes]);

  const onLoadMore = () => {
    if (!nextCursor || loading) return;
    setLoading(true);
    setError(null);
    apiClient.decors
      .list(kind, {
        subcategories: [...appliedSubs],
        brands: [...appliedBrands],
        types: [...appliedTypes],
        cursor: nextCursor,
        limit: DEFAULT_LIMIT,
      })
      .then(
        (res) => {
          setItems((prev) => [...prev, ...res.items]);
          setNextCursor(res.nextCursor);
          setLoading(false);
        },
        (err: unknown) => {
          // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
          const apiErr =
            err instanceof ApiClientError
              ? { code: err.code, message: err.message }
              : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
          setError(apiErr);
          setLoading(false);
        },
      );
  };

  // 5 состояний (P-021). loading-флаг во время "Показать ещё" не должен
  // переключать success → loading; используем `items.length === 0` как guard.
  const state = useMemo<DecorsCatalogState>(() => {
    if (error) return 'error';
    if (loading && items.length === 0) return 'loading';
    if (!hasFilters && items.length === 0) return 'idle';
    if (items.length === 0) return 'empty';
    return 'success';
  }, [error, loading, items.length, hasFilters]);

  const onToggle = (field: DecorFilterField, value: string) => {
    const toggle = (prev: readonly string[]): readonly string[] =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
    if (field === 'subcategories') setSelectedSubs(toggle);
    else if (field === 'brands') setSelectedBrands(toggle);
    else setSelectedTypes(toggle);
  };

  const onResetGroup = (field: DecorFilterField) => {
    if (field === 'subcategories') setSelectedSubs([]);
    else if (field === 'brands') setSelectedBrands([]);
    else setSelectedTypes([]);
  };

  const onSearch = () =>
    apply({
      subcategories: [...selectedSubs],
      brands: [...selectedBrands],
      types: [...selectedTypes],
    });

  const onResetAll = () => {
    setSelectedSubs([]);
    setSelectedBrands([]);
    setSelectedTypes([]);
    reset();
  };

  return (
    <DecorsCatalogView
      kind={kind}
      state={state}
      items={items}
      nextCursor={nextCursor}
      error={error ?? undefined}
      imageUrls={imageUrls}
      subcategoryOptions={subcategoryOptions}
      brandOptions={brandOptions}
      typeOptions={typeOptions}
      facetsLoading={facetsLoading}
      selectedSubcategories={selectedSubs}
      selectedBrands={selectedBrands}
      selectedTypes={selectedTypes}
      onToggle={onToggle}
      onResetGroup={onResetGroup}
      onSearch={onSearch}
      onResetAll={onResetAll}
      onLoadMore={onLoadMore}
      onItemClick={(globalId) => {
        // ADR-0055: несём применённый фильтр в URL карточки → дальше в альтернативы.
        const qs = decorFilterToQuery({
          subcategories: [...appliedSubs],
          brands: [...appliedBrands],
          types: [...appliedTypes],
        });
        navigate(
          `/decors/${encodeURIComponent(kind)}/${encodeURIComponent(globalId)}${qs ? `?${qs}` : ''}`,
        );
      }}
    />
  );
}
